import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  CalendarOff,
  ClipboardX,
  Crown,
  ListChecks,
  Timer,
  Trophy,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { METRIC_CARD_ICON_STROKE_WIDTH } from "../shared/constants";
import {
  TEAM_ICON_COLOR,
  TEAM_ICON_SIZE,
  TEAM_PODIUM_CARD_GAP,
  TEAM_PODIUM_CARD_PADDING,
  TEAM_PODIUM_CARD_RADIUS,
  TEAM_PODIUM_AVATAR_SIZE,
  TEAM_PODIUM_METRIC_LABEL_GAP,
  TEAM_PODIUM_RANK_BADGE_SIZE,
  TEAM_PODIUM_RANK_STYLES,
  TEAM_PODIUM_WINNER_BADGE_COLOR,
  TEAM_PODIUM_WINNER_CROWN_COLOR,
  TEAM_PODIUM_WINNER_NAME_BADGE_GAP,
  TEAM_PODIUM_NAME_COLOR,
} from "./constants";
import type { TeamSegment, TeamStaffRow } from "./types";

interface PodiumMetricConfig {
  key: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  getValue: (row: TeamStaffRow) => string;
  label: string;
}

function usePodiumMetricsTop(): PodiumMetricConfig[] {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: "tasksCompleted",
        icon: ListChecks,
        getValue: (row) => String(row.tasksCompleted),
        label: t("analytics.team.podium.completed", "completed"),
      },
      {
        key: "tasksCancelled",
        icon: ClipboardX,
        getValue: (row) => String(row.tasksCancelled),
        label: t("analytics.team.podium.cancelled", "cancelled"),
      },
      {
        key: "avgTimeOnTask",
        icon: Timer,
        getValue: (row) => row.avgTimeOnTask,
        label: t("analytics.team.podium.avgTime", "avg. time"),
      },
    ],
    [t],
  );
}

function usePodiumMetricsBottom(): PodiumMetricConfig[] {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: "delays",
        icon: CalendarClock,
        getValue: (row) => String(row.delays),
        label: t("analytics.team.podium.delays", "delays"),
      },
      {
        key: "daysOff",
        icon: CalendarOff,
        getValue: (row) => String(row.daysOff),
        label: t("analytics.team.podium.dayOff", "day off"),
      },
    ],
    [t],
  );
}

type PodiumRank = 1 | 2 | 3;

const PODIUM_ORDER: PodiumRank[] = [2, 1, 3];

interface TeamEmployeeOfMonthSectionProps {
  topThree: TeamStaffRow[];
  segment: TeamSegment;
  embedded?: boolean;
  onPodiumClick?: (row: TeamStaffRow) => void;
}

function PodiumMetricItem({
  metric,
  row,
}: {
  metric: PodiumMetricConfig;
  row: TeamStaffRow;
}) {
  const Icon = metric.icon;

  return (
    <div
      className="flex min-w-0 flex-col items-center text-center"
      style={{ gap: TEAM_PODIUM_METRIC_LABEL_GAP }}
    >
      <div className="flex items-center justify-center gap-2">
        <Icon
          className="shrink-0"
          style={{
            color: TEAM_ICON_COLOR,
            width: TEAM_ICON_SIZE,
            height: TEAM_ICON_SIZE,
          }}
          strokeWidth={METRIC_CARD_ICON_STROKE_WIDTH}
        />
        <span className="text-sm font-semibold leading-none text-[#171717]">
          {metric.getValue(row)}
        </span>
      </div>
      <span className="text-[10px] font-normal leading-none text-[#737373]">
        {metric.label}
      </span>
    </div>
  );
}

function PodiumMetricsGrid({
  row,
  segment,
}: {
  row: TeamStaffRow;
  segment: TeamSegment;
}) {
  const podiumMetricsTop = usePodiumMetricsTop();
  const podiumMetricsBottom = usePodiumMetricsBottom();

  if (segment === "teams") {
    return (
      <div
        className="grid w-full grid-cols-3 justify-items-center"
        style={{ gap: TEAM_PODIUM_CARD_GAP }}
      >
        {podiumMetricsTop.map((metric) => (
          <PodiumMetricItem key={metric.key} metric={metric} row={row} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col" style={{ gap: TEAM_PODIUM_CARD_GAP }}>
      <div
        className="grid w-full grid-cols-3 justify-items-center"
        style={{ gap: TEAM_PODIUM_CARD_GAP }}
      >
        {podiumMetricsTop.map((metric) => (
          <PodiumMetricItem key={metric.key} metric={metric} row={row} />
        ))}
      </div>
      <div
        className="grid w-full grid-cols-2 justify-items-center"
        style={{ gap: TEAM_PODIUM_CARD_GAP }}
      >
        {podiumMetricsBottom.map((metric) => (
          <PodiumMetricItem key={metric.key} metric={metric} row={row} />
        ))}
      </div>
    </div>
  );
}

function handlePodiumCardKeyDown(
  event: React.KeyboardEvent,
  onClick: (row: TeamStaffRow) => void,
  row: TeamStaffRow,
) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onClick(row);
  }
}

function PodiumCardHeader({
  row,
  rank,
  styles,
  showAvatar,
  isWinner,
  winnerBadgeLabel,
}: {
  row: TeamStaffRow;
  rank: PodiumRank;
  styles: (typeof TEAM_PODIUM_RANK_STYLES)[PodiumRank];
  showAvatar: boolean;
  isWinner: boolean;
  winnerBadgeLabel: string | null;
}) {
  return (
    <div
      className="flex flex-col items-center"
      style={{ gap: TEAM_PODIUM_CARD_GAP }}
    >
      <span
        className="inline-flex items-center justify-center rounded-full text-lg font-semibold leading-none"
        style={{
          width: TEAM_PODIUM_RANK_BADGE_SIZE,
          height: TEAM_PODIUM_RANK_BADGE_SIZE,
          backgroundColor: styles.circleBg,
          color: styles.circleText,
        }}
      >
        {rank}
      </span>

      {showAvatar ? (
        <Avatar
          className="shrink-0 rounded-2xl"
          style={{
            width: TEAM_PODIUM_AVATAR_SIZE,
            height: TEAM_PODIUM_AVATAR_SIZE,
          }}
        >
          {row.photoUrl ? (
            <AvatarImage src={row.photoUrl} alt={row.name} />
          ) : null}
          <AvatarFallback className="rounded-2xl text-base font-medium">
            {row.initials}
          </AvatarFallback>
        </Avatar>
      ) : null}

      <div
        className="flex flex-col items-center text-center"
        style={{
          gap: isWinner
            ? TEAM_PODIUM_WINNER_NAME_BADGE_GAP
            : TEAM_PODIUM_CARD_GAP,
        }}
      >
        <span
          className="text-center text-lg font-medium leading-none"
          style={{ color: TEAM_PODIUM_NAME_COLOR }}
        >
          {row.name}
        </span>
        {isWinner && winnerBadgeLabel ? (
          <span
            className="inline-flex items-center justify-center gap-1 text-center text-lg font-medium leading-none"
            style={{ color: TEAM_PODIUM_WINNER_BADGE_COLOR }}
          >
            <Crown
              className="shrink-0"
              style={{
                color: TEAM_PODIUM_WINNER_CROWN_COLOR,
                width: TEAM_ICON_SIZE,
                height: TEAM_ICON_SIZE,
              }}
            />
            {winnerBadgeLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function PodiumCard({
  row,
  segment,
  winnerBadgeLabel,
  onClick,
}: {
  row: TeamStaffRow;
  segment: TeamSegment;
  winnerBadgeLabel: string | null;
  onClick?: (row: TeamStaffRow) => void;
}) {
  const rank = row.rank as PodiumRank;
  const styles = TEAM_PODIUM_RANK_STYLES[rank];
  const isWinner = rank === 1;
  const showAvatar = segment === "staff";
  const handleClick = onClick ? () => onClick(row) : undefined;
  const handleKeyDown = onClick
    ? (event: React.KeyboardEvent) =>
        handlePodiumCardKeyDown(event, onClick, row)
    : undefined;

  return (
    <article
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "flex min-w-0 flex-1 flex-col border-2",
        isWinner && "z-10 self-stretch",
        onClick &&
          "cursor-pointer transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#826BFF]",
      )}
      style={{
        gap: TEAM_PODIUM_CARD_GAP,
        padding: TEAM_PODIUM_CARD_PADDING,
        borderRadius: TEAM_PODIUM_CARD_RADIUS,
        backgroundColor: styles.background,
        borderColor: styles.border,
      }}
    >
      <PodiumCardHeader
        row={row}
        rank={rank}
        styles={styles}
        showAvatar={showAvatar}
        isWinner={isWinner}
        winnerBadgeLabel={winnerBadgeLabel}
      />

      <PodiumMetricsGrid row={row} segment={segment} />
    </article>
  );
}

export const TeamEmployeeOfMonthSection: React.FC<
  TeamEmployeeOfMonthSectionProps
> = ({ topThree, segment, embedded = false, onPodiumClick }) => {
  const { t } = useTranslation();
  const isStaffSegment = segment === "staff";
  const title = isStaffSegment
    ? t("analytics.team.employeeOfMonth", "Employee of the month")
    : t("analytics.team.teamOfMonth", "Team of the month");
  const winnerBadgeLabel = isStaffSegment
    ? t("analytics.team.employeeOfMonth", "Employee of the month")
    : t("analytics.team.teamOfMonth", "Team of the month");

  const orderedRows = PODIUM_ORDER.map((rank) =>
    topThree.find((row) => row.rank === rank),
  ).filter((row): row is TeamStaffRow => row !== undefined);

  return (
    <RevenueChartCard
      title={title}
      icon={<Trophy />}
      iconColor={TEAM_ICON_COLOR}
      iconSize={TEAM_ICON_SIZE}
      className={cn("w-full", embedded && "border-0 shadow-none rounded-none")}
      style={embedded ? { padding: 0 } : undefined}
    >
      <div className="flex w-full items-end gap-4 pt-1">
        {orderedRows.map((row) => (
          <PodiumCard
            key={row.id}
            row={row}
            segment={segment}
            winnerBadgeLabel={winnerBadgeLabel}
            onClick={onPodiumClick}
          />
        ))}
      </div>
    </RevenueChartCard>
  );
};
